import {
  type CheckFinding,
  type CheckOutcome,
  type CheckResult,
  RELEASE_STATE_NAMES,
  type ReleaseState,
  SUBMISSION_CHECKS,
  SUBMISSION_STATUS_NAMES,
  type SubmissionStatus,
  type SubmissionView,
} from '@mogzo/shared';
import { useState } from 'react';

// Pieces the Submissions pages share (M21): status chips, check marks and findings.

/** Where a version stands: its submission status, or once approved, where publishing took it. */
export type VersionState = SubmissionStatus | ReleaseState | 'scheduled';

const STATUS_STYLES: Record<VersionState, string> = {
  uploading: 'bg-field text-muted',
  checking: 'bg-field text-muted',
  failed_checks: 'bg-danger-soft text-danger',
  in_review: 'bg-info-soft text-info',
  changes_requested: 'bg-warning-soft text-warning',
  approved: 'bg-success-soft text-success',
  rejected: 'bg-danger-soft text-danger',
  withdrawn: 'bg-field text-muted',
  scheduled: 'bg-info-soft text-info',
  live: 'bg-success-soft text-success',
  replaced: 'bg-field text-muted',
  rolled_back: 'bg-warning-soft text-warning',
  skipped: 'bg-field text-muted',
};

const STATE_NAMES: Record<VersionState, string> = {
  ...SUBMISSION_STATUS_NAMES,
  ...RELEASE_STATE_NAMES,
  scheduled: 'Scheduled',
};

/** A submission's state for its chip: an approved one waiting for its set time is Scheduled. */
export function versionState(submission: SubmissionView): VersionState {
  if (submission.release) return submission.release;
  if (submission.status === 'approved' && submission.publishAt) return 'scheduled';
  return submission.status;
}

export function StatusChip({ status }: { status: VersionState }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${STATUS_STYLES[status]}`}
    >
      {STATE_NAMES[status]}
    </span>
  );
}

/** What each status means, for the Submissions page's side card. */
export const STATUS_HELP: [VersionState, string][] = [
  ['checking', 'MOGZO scans the build and source. A few minutes.'],
  ['failed_checks', 'Something to fix before a person looks. The report says what.'],
  ['in_review', 'Waiting for, or with, a reviewer.'],
  ['changes_requested', 'Fix what the reviewer noted and send it again.'],
  ['approved', 'Passed review. Publish it now or at a set time.'],
  ['scheduled', 'Goes live at the time you picked. You can change or cancel it until then.'],
  ['live', 'What players get now.'],
  ['replaced', 'Was live until a newer version took over. MOGZO can roll back to it.'],
  ['rolled_back', 'MOGZO took it off and brought the version before back. Send a higher version.'],
  ['skipped', 'A newer version was approved before this one went live.'],
  ['rejected', 'This version won’t go live. The reviewer says why.'],
  ['withdrawn', 'You took it back. It stays here as a record.'],
];

const MARK_STYLES: Record<CheckOutcome | 'warn' | 'fail', string> = {
  passed: 'bg-success-soft text-success',
  warnings: 'bg-warning-soft text-warning',
  warn: 'bg-warning-soft text-warning',
  failed: 'bg-danger-soft text-danger',
  fail: 'bg-danger-soft text-danger',
  skipped: 'bg-field text-muted',
};

const TICK = (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** A round mark: a tick, “!” for a warning, “×” for a failure, “–” when skipped. */
export function Mark({ kind }: { kind: CheckOutcome | 'warn' | 'fail' }) {
  const symbol =
    kind === 'passed'
      ? TICK
      : kind === 'failed' || kind === 'fail'
        ? '×'
        : kind === 'skipped'
          ? '–'
          : '!';
  return (
    <span
      aria-hidden="true"
      className={`mt-px grid size-[22px] flex-none place-items-center rounded-full text-[13px] font-extrabold ${MARK_STYLES[kind]}`}
    >
      {symbol}
    </span>
  );
}

export const OUTCOME_NAMES: Record<CheckOutcome, string> = {
  passed: 'Passed',
  warnings: 'Warnings',
  failed: 'Failed',
  skipped: 'Skipped',
};

export const checkName = (id: CheckResult['id']) =>
  SUBMISSION_CHECKS.find((check) => check.id === id)?.name ?? id;

/** “All 9 passed”, “2 failed”, “Passed, 1 with warnings”. */
export function checksSummary(checks: CheckResult[]): string {
  const failed = checks.filter((check) => check.outcome === 'failed').length;
  const warned = checks.filter((check) => check.outcome === 'warnings').length;
  if (failed) return `${failed} failed`;
  if (warned) return `Passed, ${warned} with warnings`;
  return `All ${checks.length} passed`;
}

/** One finding: where it is (a file and line), what it says, and a piece of the code. */
export function Finding({ finding }: { finding: CheckFinding }) {
  return (
    <li className="flex flex-col gap-1 text-[13px]">
      {finding.file && (
        <span className="font-mono text-[12.5px] font-medium break-all text-ink">
          {finding.file}
          {finding.line ? <span className="text-muted"> line {finding.line}</span> : null}
        </span>
      )}
      <span className="leading-normal">{finding.message}</span>
      {finding.excerpt && (
        <code className="block overflow-x-auto rounded-lg bg-field px-2.5 py-1.5 font-mono text-[12px] whitespace-pre text-muted">
          {finding.excerpt}
        </code>
      )}
    </li>
  );
}

/** A check's findings, the first few shown and the rest a click away. */
export function Findings({ check }: { check: CheckResult }) {
  const [all, setAll] = useState(false);
  const shown = all ? check.findings : check.findings.slice(0, 3);
  const hidden = check.findings.length - shown.length;
  if (!check.findings.length) return null;
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-3">
        {shown.map((finding, i) => (
          // Findings never change order once made, so their place is their key.
          // biome-ignore lint/suspicious/noArrayIndexKey: see above
          <Finding key={i} finding={finding} />
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setAll(true)}
          className="self-start text-[13px] font-semibold underline decoration-brand decoration-2 underline-offset-4"
        >
          Show {hidden} more
        </button>
      )}
      {all && check.moreFindings ? (
        <p className="text-[13px] text-muted">
          …and {check.moreFindings} more like these, left out of the report.
        </p>
      ) : null}
    </div>
  );
}

const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const dayMonth = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dayMonthYear = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** “Today, 10:42”, “24 Oct”, “24 Oct 2025”; with `at`, the time too: “24 Oct at 14:05”. */
export function when(iso: string, { at = false, now = new Date() } = {}): string {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) return `Today, ${time.format(date)}`;
  const day = (date.getFullYear() === now.getFullYear() ? dayMonth : dayMonthYear).format(date);
  return at ? `${day} at ${time.format(date)}` : day;
}

/** “in 3 minutes”, from when checks started to when they finished. */
export function took(from: string | null, to: string | null): string | null {
  if (!from || !to) return null;
  const minutes = Math.max(1, Math.round((Date.parse(to) - Date.parse(from)) / 60_000));
  return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
}
